/**
 * useSectionGraph — the one place that knows what order a shop's rooms are in.
 *
 * A shop is scanned as several SECTIONS, and until now nothing in the viewer treated them as
 * parts of one place: the drawer listed them by camera count, which is a property of the
 * reconstruction, not an arrangement anyone chose.
 *
 * There is no geometry to appeal to. Every section is an independent COLMAP reconstruction in
 * its own arbitrary coordinate frame AND scale, and nothing anywhere aligns them — so "which
 * room is through that wall" is not a question the scan data can answer.
 *
 * TWO KINDS OF SECTION, and both have to be here, because the SectionsDrawer already switches
 * on both and a chip that disagreed with the drawer about what "2 of 5" means would be worse
 * than no chip:
 *
 *   user    rooms the owner named in ScanCapture. String ids, one dataset each, ordered by the
 *           owner's `shop.sections[].order`. Switched with loadSectionDataset.
 *   colmap  disconnected components the reconstruction found in ONE scan. Numeric ids, no
 *           owner-chosen order at all. Switched with loadSectionAsIndependentShop.
 *
 * Everything downstream — the section chip, the depth viewer's doorway — asks this hook and
 * nothing else. Replacing the ordering with author-declared links ({fromSection, fromFrame,
 * toSection, toFrame}) later means changing this file and no other.
 */
import { useMemo } from 'react'
import { useAppStore } from '@/store/appStore'
import { useAuthStore } from '@/stores/authStore'
import { colmapSectionThumbnail, userSectionThumbnail } from '@/utils/sectionThumbnail'

export interface SectionNode {
  /** Stable identity, for React keys and lookup. Not an id the store understands — see kind. */
  key: string
  kind: 'user' | 'colmap'
  /** Set when kind === 'user'. What loadSectionDataset takes. */
  userSectionId?: string
  /** Set when kind === 'colmap'. What loadSectionAsIndependentShop takes. */
  colmapSectionId?: number
  name: string
  /** Poster image, shown in the chip and painted into the doorway. '' if underivable. */
  posterUrl: string
  /**
   * How many frames this section has, 0 when unknown.
   *
   * Needed to walk BACKWARDS through a doorway. Sections join end to start, so entering the
   * previous room means arriving at its LAST frame — you came in through its far end — and that
   * index has to be known before the section is loaded, or the arrival is a jump cut to frame 0.
   */
  frameCount: number
  isActive: boolean
}

export interface SectionGraph {
  ordered: SectionNode[]
  /** Position of the open section in `ordered`, or -1 while nothing is resolved yet. */
  currentIndex: number
  total: number
  /** The rooms either side. Null at the ends — the sequence does NOT wrap; see below. */
  next: SectionNode | null
  prev: SectionNode | null
}

const EMPTY: SectionGraph = { ordered: [], currentIndex: -1, total: 0, next: null, prev: null }

export function useSectionGraph(): SectionGraph {
  const shopDatasets = useAppStore((s) => s.shopDatasets)
  const activeUserSectionId = useAppStore((s) => s.activeUserSectionId)
  const sections = useAppStore((s) => s.sections)
  const currentSectionId = useAppStore((s) => s.currentSectionId)
  const shopMetadata = useAppStore((s) => s.shopMetadata)
  const availableDatasets = useAppStore((s) => s.availableDatasets)
  const shopId = useAppStore((s) => s.currentShopId)
  const authToken = useAuthStore((s) => s.token)

  return useMemo(() => {
    // Same test the drawer uses to pick which list to render, so the two never disagree about
    // which kind of section this shop has.
    const userDatasets = shopDatasets.filter((d) => d.sectionId)
    const hasUserSections = shopDatasets.length > 1 && userDatasets.length > 0

    let ordered: SectionNode[]

    if (hasUserSections) {
      // `order` only reaches the client on the mobile/public-shop path; datasets.json carries no
      // such field. Requiring EVERY section to have one before using it keeps a shop from being
      // half-arranged and half-sorted, which would put the rooms in an order matching neither.
      //
      // The fallback is the section id, NOT camera count. Camera count is how well a section
      // reconstructed, which has nothing to do with where it is — ordering rooms by it means
      // "next" lands somewhere arbitrary, and a doorway that opens onto an arbitrary room is
      // not a doorway.
      const hasOrder = userDatasets.every((d) => typeof d.order === 'number')
      const sorted = [...userDatasets].sort((a, b) =>
        hasOrder
          ? (a.order as number) - (b.order as number)
          : String(a.sectionId).localeCompare(String(b.sectionId)),
      )
      ordered = sorted.map((d) => ({
        key: `user:${d.sectionId}`,
        kind: 'user' as const,
        userSectionId: d.sectionId!,
        name: d.sectionName || d.name || `Section ${d.sectionId}`,
        posterUrl: userSectionThumbnail(d, authToken),
        frameCount: d.camera_count ?? 0,
        isActive: d.sectionId === activeUserSectionId,
      }))
    } else if (sections.length > 1) {
      // By SECTION NUMBER, ascending. Nobody arranged these — they are whatever the
      // reconstruction split the scan into — so the id is the only ordering with any meaning,
      // and it is at least the order they were captured in. Sorting by camera count instead
      // (which is what the drawer used to do) puts "next" wherever the best reconstruction
      // happens to be: section 1 → section 4 → section 2, which is not walking anywhere.
      const sorted = [...sections].sort((a, b) => a.sectionId - b.sectionId)
      ordered = sorted.map((s, i) => ({
        key: `colmap:${s.sectionId}`,
        kind: 'colmap' as const,
        colmapSectionId: s.sectionId,
        name:
          shopMetadata?.sections.find((m) => m.section_id === s.sectionId)?.name ||
          `Section ${i + 1}`,
        posterUrl: colmapSectionThumbnail(
          shopId, s.sectionId, shopMetadata, availableDatasets, authToken,
        ),
        frameCount: s.frameCount ?? 0,
        isActive: s.sectionId === currentSectionId,
      }))
    } else {
      return EMPTY
    }

    if (ordered.length < 2) return EMPTY
    const currentIndex = ordered.findIndex((n) => n.isActive)

    // No wrap. The morph viewer wraps FRAMES modulo the count, but a room sequence that puts you
    // back at the entrance when you walk out of the last aisle reads as a bug, not a loop — and
    // in the doorway it would mean a wall that is never actually a wall.
    return {
      ordered,
      currentIndex,
      total: ordered.length,
      next: currentIndex >= 0 ? ordered[currentIndex + 1] ?? null : null,
      prev: currentIndex > 0 ? ordered[currentIndex - 1] : null,
    }
  }, [
    shopDatasets, activeUserSectionId, sections, currentSectionId,
    shopMetadata, availableDatasets, shopId, authToken,
  ])
}
